<template>
  <parts-product-page-content
    v-if="pageData?.databaseId"
    :page-data="pageData"
    :show-skeleton-loading="isLoading"
    :isPreview="isPreview"
  />
  <parts-password-protected-form
    v-if="! pageData?.databaseId && ! isLoading && passwordProtectedData?.isPasswordProtected"
    :post-id="passwordProtectedData.productId"
    submit-button-text="View Product"
    @access-successful="passwordAccessSuccessful"
  />
  <not-found v-if="notFound" />
</template>

<script>
import {
  fetchProductData,
  getProductTypeByUri,
  getProductPreviewById,
  getPrivateProductById
} from '~/hooks/queries/use-product-data-query.js';
import { loadHeadMeta } from '~/composables/headmeta';
import { useUmami } from '~/composables/useUmami';
import NotFound from '~/components/NotFound.vue';
import {
  useNuxtApp,
  useRoute,
  ref,
  useProductStore,
  reloadNuxtApp,
  setResponseStatus,
  useRequestEvent,
  useState,
} from '#imports';

/**
 * Fire a `view_item` umami event for a single-product page-load.
 *
 * Prices MUST be numbers in the shop's base currency, matching the server-side events the other
 * shops emit (`audiogroup-umami-events.php`). Umami stores strings and numbers in separate columns
 * — a formatted string here lands in `event_data.string_value` while Boom/Sonuscore land in
 * `number_value`, so every price report silently skips this shop. No error, just a total that is
 * too low. That was the case until 2026-07-16.
 *
 * The numbers come from `defaultCurrency.*Float`, resolved server-side: WooCommerce and Aelia stay
 * authoritative (incl. per-vendor base currencies and per-currency overrides), we only read. Never
 * parse the formatted strings — for a variable product `price` reads "135,00&nbsp;€ - 255,00&nbsp;€",
 * which is a range with an HTML entity, not a price.
 *
 * Still missing vs. Boom/Sonuscore: original_price / original_currency / exchange_rate (would need
 * the visitor-currency conversion exposed too), product_sku, product_type, in_stock. Those are
 * absent, not wrong — they skew nothing.
 */
const fireViewItem = (product) => {
  if (!product?.databaseId) return

  const categories = (product.productCategories?.nodes ?? [])
    .map((n) => n?.name)
    .filter(Boolean)
    .join(',')

  const currency = product.defaultCurrency ?? {}

  useUmami().track('view_item', {
    product_id: product.databaseId,
    product_name: product.name ?? null,
    // Numbers, base currency — same semantics as the server-side events elsewhere.
    price: currency.priceFloat ?? null,
    regular_price: currency.regularPriceFloat ?? null,
    // null when not on sale — absence is the signal, do not coerce it to 0.
    sale_price: currency.salePriceFloat ?? null,
    // Base currency ISO code, e.g. "USD" on ASFX.
    currency: currency.code ?? null,
    categories,
  })
}

export default {
  components: {
    NotFound,
  },
  async setup() {
    const route = useRoute()
    const app = useNuxtApp()
    const productStore = useProductStore()
    const requestEvent = import.meta.server ? useRequestEvent() : null
    // useState so the server decision reaches the client payload (no hydration mismatch)
    const notFound = useState(`product-not-found:${route.path}`, () => false)

    const isLoading = ref(true)
    const pageData = ref({});
    const isPreview = ref(false)
    const passwordProtectedData = ref({})
    let checkProductType = false

    // If preview - prevent SSR fetch and wait to fetch preview in client
    if (route?.query?.preview && route?.query?.preview_id) {
      isPreview.value = true
      return {
        pageData,
        isLoading
      }
    }

    // Client Only - if basic product details already available, then fetch basic details from store while full content loads
    if (import.meta.client) {
      pageData.value = productStore.getBasicProductDetails
    }

    const productRequest = fetchProductData(route.path)
      .then(({ data, errors }) => {
        if (data?.nodeByUri?.seo) {
          app.runWithContext(() => {
            loadHeadMeta(data.nodeByUri)
          })
        }

        // No product behind this URI at all (not just password protected): real 404 for crawlers.
        // A GraphQL error is not proof of a missing product, so it never yields a 404.
        notFound.value = !! data && ! errors?.length
          && ! data.nodeByUri?.databaseId && ! data.productTypeByUri?.productId

        if (requestEvent && notFound.value) {
          setResponseStatus(requestEvent, 404)
        }

        checkProductType = ! data?.nodeByUri?.databaseId && ! notFound.value

        if (checkProductType) {
          getProductTypeByUri(route.path).then(({ data }) => {
            // If product is password protected, set data to show password form
            if (data.productTypeByUri.isPasswordProtected) {
              passwordProtectedData.value = {
                isPasswordProtected: true,
                productId: data.productTypeByUri.productId
              }

              try {
                // if cookie for product password exists, attempt to fetch product
                getPrivateProductById({ productId: parseInt(data.productTypeByUri.productId, 10) })
                  .then(({ productPrivate }) => {
                    if (productPrivate) {
                      pageData.value = productPrivate
                      fireViewItem(productPrivate)
                      return
                    }
                  }).finally(() => {
                    isLoading.value = false
                })
              } catch (err) {
                console.error(err)
              }
            } else {
              isLoading.value = false
            }
          })
        }

        pageData.value = data.nodeByUri ?? null
        fireViewItem(data?.nodeByUri)
      })
      .catch((err) => {
        console.error(err)

        // Backend unreachable: 503 so crawlers retry instead of indexing an empty page
        if (requestEvent) {
          setResponseStatus(requestEvent, 503)
        }
      })
      .finally(() => {
        if (! checkProductType) {
          isLoading.value = false
        }

        if (import.meta.client) {
          productStore.removeBasicProductDetails()
        }
      })

    // The server must know the result before it sends status and HTML
    if (import.meta.server) {
      await productRequest
    }

    return {
      pageData,
      isLoading,
      passwordProtectedData,
      notFound,
    };
  },
  data() {
    return {
      isPreview: false,
      inputPassword: '',
    }
  },
  mounted() {
    const route = useRoute()

    if (route?.query?.preview && route?.query?.preview_id) {
      this.isPreview = true;
      const previewId = route.query.preview_id
      try {
        getProductPreviewById(parseInt(previewId, 10)).then(({ productPreview }) => {

          if (productPreview && productPreview !== null) {
            this.pageData = productPreview
          } else {
            this.isPreview = false
          }
        }).finally(() => {
          this.isLoading = false
        })
      } catch (err) {
        console.error(err)
        this.isPreview = false;
      }
    }
  },
  methods: {
    passwordAccessSuccessful() {
      reloadNuxtApp()
    },
  },
  watch: {
    isLoading(newVal) {
      if (newVal === false && this.pageData == null && ! this.passwordProtectedData?.isPasswordProtected) {
        this.notFound = true;
      }
    }
  }
}
</script>
